import React from 'react';
import { Link } from 'react-router-dom';
import type { Equipamento } from '../../../../models';
import { useHistoricoEquipamentoQuery } from '../../../../hooks/data';
import RecuperarHistoricoBarra from './RecuperarHistoricoBarra';
import type { ContextoAtivos } from './ativos.utils';
import { descreverEvento, formatarDataHora } from './ativoHistorico.format';

/**
 * Aba "Histórico" da ficha — linha do tempo do ativo, mais recente primeiro: quem mudou o
 * quê, quando e de onde veio (manual, Movimentação, Estadia). Gravado pelo servidor
 * (onEquipamentoWriteHistorico); só leitura aqui. Eventos anteriores à existência do
 * histórico não aparecem até serem recuperados das Solicitações antigas.
 */
export default function AtivoHistorico({ equipamento, contexto }: { equipamento: Equipamento; contexto: ContextoAtivos }) {
  const { data: eventos, isLoading, erro } = useHistoricoEquipamentoQuery(equipamento.id);
  // Cadastro que nasceu com o histórico já existindo não tem passado a buscar.
  const barra = !equipamento.historicoDesdeCriacao && <RecuperarHistoricoBarra equipamento={equipamento} />;

  if (isLoading) return <p className="text-sm text-gray-500">Carregando...</p>;
  if (erro) {
    return (
      <>
        {barra}
        <p className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {erro === 'sem-permissao'
            ? 'Não foi possível ler o histórico: falta a regra de acesso do histórico no Firestore deste ambiente (ela sobe junto com o deploy desta versão).'
            : `Não foi possível ler o histórico: ${erro}`}
        </p>
      </>
    );
  }
  if (eventos.length === 0) {
    return (
      <>
        {barra}
        <p className="rounded-md border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
          Nenhum evento registrado ainda. O histórico começa a ser gravado a partir da próxima alteração deste ativo.
        </p>
      </>
    );
  }

  return (
    <>
    {barra}
    <ol className="relative space-y-4 border-l border-gray-200 pl-5">
      {eventos.map((evento) => {
        const descrito = descreverEvento(evento, contexto);
        return (
          <li key={evento.id} className="relative">
            <span className="absolute -left-[26px] top-1.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-blue-500" />
            <p className="text-sm font-medium text-gray-900">{descrito.titulo}</p>
            {descrito.linhas.map((linha, i) => (
              <p key={i} className="whitespace-pre-wrap text-sm text-gray-600">
                {linha}
              </p>
            ))}
            <p className="mt-0.5 text-xs text-gray-400">
              {formatarDataHora(evento.dataEvento ?? evento.em)} · {descrito.origem}
              {descrito.solicitacaoId && (
                <>
                  {' · '}
                  <Link to={`/si/solicitacoes/${descrito.solicitacaoId}`} className="text-blue-600 hover:underline" target="_blank">
                    ver Solicitação
                  </Link>
                </>
              )}
            </p>
          </li>
        );
      })}
    </ol>
    </>
  );
}
